import React from "react";
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { PendingReceiptDocumentDraft } from "models/propTypes/receiptPropTypes";
import { ReceiptFormTypes } from "models/formTypes";
import { FormFieldConstants } from "utils/constants";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//endregion
//#region components
import { Grid } from "@mui/material";
import TopTodoCard from "../../_partials/topTodoCard";
import { FileViewerContainer, GenericCommentsView, TitledContainerInner, EntityCardContent } from "components/common";
import { HighReceiptAmountFloatingMessage, ReceiptUploadedBy, useReceiptAttachments } from "components/receipt";
import { useReceiptViewInfo } from "components/receipt";
import {
  ReceiptDraftFooter,
  ReceiptDraftTodoSelectionFormContainer,
  useReceiptDraftFormValidations,
  AutomationRuleCreateForm,
} from "components/receiptDraft";
import { useReceiptService } from "serviceHooks";
//#endregion
//#region services
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const ReceiptDraftTodo: React.FC<TodoCardProps> = (props) => {
  const receiptService = useReceiptService();
  const receiptDraft = props.item.receipt;

  const [loading, setLoading] = React.useState(false);
  const [isRuleCreateView, setIsRuleCreateView] = React.useState(false);
  const [showRuleCreateButton, setShowRuleCreateButton] = React.useState(false);
  const [pendingDocumentDraft, setPendingDocumentDraft] = React.useState<PendingReceiptDocumentDraft>();

  React.useEffect(() => {
    setShowRuleCreateButton(
      (receiptDraft?.isMatchedToDocumentDraftViaAutomationService && !!receiptDraft?.documentDraftId) ?? false
    );
  }, [receiptDraft?.isMatchedToDocumentDraftViaAutomationService, receiptDraft?.documentDraftId]);

  const receiptDraftSubmitFormRef = React.useRef<FormHandles>(null);
  const ruleCreateSubmitFormRef = React.useRef<FormHandles>(null);
  const { receiptDraftValidationScheme } = useReceiptDraftFormValidations();

  const { attachments, onUploadedFilesChange, getLoadedData } = useReceiptAttachments(
    receiptDraft!,
    receiptDraftSubmitFormRef,
    FormFieldConstants.Receipt.ReceiptDraftTodo.FilesToUpload
  );

  const onViewInfoClick = useReceiptViewInfo(receiptDraft!.id, receiptDraftSubmitFormRef);

  const receiptDraftSubmitForm = async (submitFunc?: (values: any) => Promise<any>, isSoftValidation?: boolean) => {
    const form = receiptDraftSubmitFormRef.current;

    // Saving a draft must not go through submit(): it runs the form's full schema first and marks
    // every empty required field red, even though a half-filled draft is allowed to be saved.
    if (isSoftValidation && form?.validate && form.getValues) {
      const isValid = await form.validate(
        TranslationKeys.Validations.FormSubmitFailed,
        receiptDraftValidationScheme(true)
      );
      if (!isValid) throw new Error("validation failed");

      // A save never links the underlag, so remember an unlinked pick and let the form put it
      // back after it reloads from the saved receipt. "Skicka in" links it.
      const values = form.getValues() as ReceiptFormTypes.ReceiptDraftTodo;
      const pending =
        receiptDraft && values.documentDraftId !== receiptDraft.documentDraftId
          ? {
              documentDraftId: values.documentDraftId,
              files: values.filesToUpload?.filter((file) => file.isDocumentDraft) ?? [],
              savedFrom: receiptDraft,
            }
          : undefined;
      const result = await submitFunc?.(values);
      setPendingDocumentDraft(pending);
      return result;
    }

    if (form) {
      return form.submit(submitFunc);
    }

    return (submitFunc && submitFunc({})) || new Promise<void>((resolve) => resolve());
  };

  const ruleCreateSubmitForm = (submitFunc?: (values: any) => Promise<any>) => {
    if (ruleCreateSubmitFormRef.current) {
      return ruleCreateSubmitFormRef.current.submit(submitFunc);
    }

    return (submitFunc && submitFunc({})) || new Promise<void>((resolve) => resolve());
  };

  const onToggleRuleCreateView = async (show: boolean) => {
    setIsRuleCreateView((prev) => {
      if (prev === show) return prev;
      return show;
    });
  };

  const toggleRuleCreateButton = (show: boolean) => {
    setShowRuleCreateButton((prev) => {
      if (prev === show) return prev;
      return show;
    });
  };

  const [selectedCategoryId, setSelectedCategoryId] = React.useState<number | undefined>(undefined);

  const onReceiptDraftCategoryChange = (category?: AutocompleteListItem) => {
    setSelectedCategoryId(category?.data?.id);

    const setFieldValue = ruleCreateSubmitFormRef.current?.setFieldValue;
    if (!setFieldValue) return;

    const categoryType = category?.data?.categoryType;
    const typeNotValid =
      categoryType !== undefined &&
      [
        WintSuperkollAPIModel.ReceiptCategoryType.Representation,
        WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation,
        WintSuperkollAPIModel.ReceiptCategoryType.Other,
      ].includes(categoryType);

    if (!typeNotValid) {
      setFieldValue(
        `${FormFieldConstants.AutomationRule.AutomationRuleCreate.ReceiptDraft}.${FormFieldConstants.AutomationRule.CreateReceiptDraftRule.Category}`,
        category
      );
    } else {
      setFieldValue(
        `${FormFieldConstants.AutomationRule.AutomationRuleCreate.ReceiptDraft}.${FormFieldConstants.AutomationRule.CreateReceiptDraftRule.Category}`,
        undefined
      );
    }
  };

  const reloadTodo = (onSuccess?: (receipt: WintSuperkollAPIModel.Receipt) => void, onError?: () => void) => {
    if (receiptDraft) {
      setLoading(true);

      const documentDraftId =
        receiptDraftSubmitFormRef.current?.getValues &&
        (receiptDraftSubmitFormRef.current.getValues() as ReceiptFormTypes.ReceiptDraftTodo).documentDraftId;

      receiptService
        .get(receiptDraft.id)
        .then((result) => {
          onSuccess && onSuccess({ ...result, documentDraftId: documentDraftId });
        })
        .catch(() => {
          onError && onError();
        })
        .finally(() => {
          setLoading(false);
          props.onActionSuccess();
        });
    }
  };

  const fileViewer = attachments?.length ? (
    <FileViewerContainer key={receiptDraft?.id} meta={attachments} loading={attachments === undefined} />
  ) : undefined;

  const getDescriptionHeadingTwo = () => {
    const arr: string[] = [];
    if (receiptDraft?.categoryName) arr.push(receiptDraft.categoryName);
    if (receiptDraft?.supplierName) arr.push(receiptDraft.supplierName);
    return arr.join(", ");
  };

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={LocalizationHelperInstance.getFormattedCurrency(
            receiptDraft?.amount ?? 0,
            receiptDraft?.currency
          )}
          descriptionHeadingTwo={getDescriptionHeadingTwo()}
          descriptionheadingThree={
            <ReceiptUploadedBy
              personId={receiptDraft?.createdByPersonId}
              personName={receiptDraft?.createdByPersonName}
            />
          }
          footerText={`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PurchaseDate)}: ${
            receiptDraft?.dateTime ? LocalizationHelperInstance.getFormattedDate(receiptDraft?.dateTime) : "-"
          }`}
          onViewAllClick={onViewInfoClick}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(
            !isRuleCreateView
              ? TranslationKeys.Receipt.FinaliseTheReceipt
              : TranslationKeys.AutomationRule.ReceiptDraftTodoHeader
          )}
          subHeader={
            isRuleCreateView
              ? LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.AutomationRule.ReceiptDraftTodoHeaderDescription
                )
              : undefined
          }
        >
          {receiptDraft && (
            <>
              <Grid container className={isRuleCreateView ? cssExports.hidden : undefined}>
                <ReceiptDraftTodoSelectionFormContainer
                  ref={receiptDraftSubmitFormRef}
                  receipt={receiptDraft}
                  pendingDocumentDraft={pendingDocumentDraft}
                  onPendingDocumentDraftApplied={() => setPendingDocumentDraft(undefined)}
                  getLoadedImageData={getLoadedData}
                  onFilesUploadChange={onUploadedFilesChange}
                  fileViewer={fileViewer}
                  toggleRuleCreateButton={toggleRuleCreateButton}
                  onReceiptCategoryChange={onReceiptDraftCategoryChange}
                />
              </Grid>
              <Grid container className={!isRuleCreateView ? cssExports.hidden : undefined}>
                <AutomationRuleCreateForm ref={ruleCreateSubmitFormRef} receipt={receiptDraft} />
              </Grid>
            </>
          )}
        </TitledContainerInner>
      }
      infoViewer={attachments?.length ? fileViewer : undefined}
      footer={
        receiptDraft ? (
          <ReceiptDraftFooter
            receipt={receiptDraft}
            receiptFormSubmitHandler={receiptDraftSubmitForm}
            reloadReceipt={reloadTodo}
            markAsDone={props.onActionSuccess}
            disableSuccessMessages
            isRuleCreateView={isRuleCreateView}
            onShowReceiptDraftTodo={onToggleRuleCreateView.bind(null, false)}
            onShowRuleCreateView={onToggleRuleCreateView.bind(null, true)}
            showRuleCreateButton={showRuleCreateButton}
            ruleCreateFormSubmitHandler={ruleCreateSubmitForm}
          />
        ) : (
          <></>
        )
      }
      loading={loading}
    >
      <GenericCommentsView parentId={receiptDraft!.id} type={WintSuperkollAPIModel.GenericCommentType.Receipt} />
      <HighReceiptAmountFloatingMessage
        amount={receiptDraft?.amount}
        currency={receiptDraft?.currency}
        receiptCategoryId={selectedCategoryId ?? receiptDraft?.receiptCategoryId}
        date={receiptDraft?.dateTime ? new Date(receiptDraft.dateTime) : undefined}
      />
    </EntityCardContent>
  );
};

export default ReceiptDraftTodo;
